import { useState, useEffect, useCallback } from "react";
import { Navigate } from "react-router-dom";
import { useSearchParams } from '@/routing/navigation';
import dynamic from '@/routing/dynamic';
import { mutate } from 'swr';
import { SWR_KEYS as BILLING_SWR_KEYS } from './billing/useBillingData';
import { toast } from 'sonner';
import { PageContent } from "@/components/layout/PageContent";
import { PageHeader } from "@/components/layout/PageHeader";
import { PageLayout } from "@/components/layout/PageLayout";
import {
  Tabs,
  TabsContent,
} from "@design-system/components/tabs";
import { Button } from "@design-system/components/button";
import { Card } from "@design-system/components/card";
import { Skeleton } from "@design-system/components/skeleton";
import { BillingLockedTooltip } from "@/components/billing/BillingLockedTooltip";
import { useTranslation } from "react-i18next";
import { AccountTab } from "./AccountTab";
import { EditProfileDialog } from "./EditProfileDialog";
import { InviteDialog } from "./InviteDialog";
import { TrialAudienceUpgradeModal } from "@/components/billing/TrialAudienceUpgradeModal";
import { useSettingsProfile } from "./useSettingsProfile";
import { useSettingsData } from "./useSettingsData";
import { resolveInviteMode } from "./inviteMode";
import BillingTab from "./billing/BillingTab";
import { useOrgMembers, useOrgInvites, TEAM_SWR_KEYS } from "./useTeamData";
import { billingApi, organizationsApi } from "@/lib/api";
import { ROUTES } from "@/routes/routes";
import { SLACK_TWINS_INSTALL_RESULT_PARAM } from "@digital-twins/shared-types";
import { useAgentHandlerEnabled } from "@/hooks/useAgentHandlerEnabled";
import { useAssistantPlatformEnabled } from "@/hooks/useAssistantPlatformEnabled";

const KnowledgeTab = dynamic(() => import("./KnowledgeTab"), {
  ssr: false,
  loading: () => (
    <TabsContent value="knowledge" className="space-y-6">
      <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
        {[1, 2, 3].map((i) => (
          <Skeleton key={i} className="h-40 rounded-2xl" />
        ))}
      </div>
    </TabsContent>
  ),
});

const BrandTab = dynamic(() => import("./BrandTab"), {
  ssr: false,
  loading: () => (
    <TabsContent value="brand" className="space-y-6">
      <Card className="p-6">
        <Skeleton className="h-6 w-40 mb-6" />
        <Skeleton className="h-64 w-full rounded-xl" />
      </Card>
    </TabsContent>
  ),
});

const SkillsTab = dynamic(() => import("./SkillsTab"), {
  ssr: false,
  loading: () => (
    <TabsContent value="skills" className="space-y-6">
      <Card className="p-6">
        <Skeleton className="h-6 w-40 mb-6" />
        <Skeleton className="h-64 w-full rounded-xl" />
      </Card>
    </TabsContent>
  ),
});

/**
 * Assistant Platform v1 Step 3.2: exported (rather than kept file-private) so
 * it can be unit-tested directly instead of round-tripping through a full
 * render of this page's many hooks just to prove `?tab=skills` resolves to
 * the skills tab instead of silently falling back to `account`.
 */
export function normalizeSettingsTabParam(tab: string | null): string {
  if (!tab) return "account";
  const allowed = ["account", "knowledge", "brand", "billing", "skills", "connectors"];
  return allowed.includes(tab) ? tab : "account";
}

/**
 * Frontend review BLOCKER 1 (item 6): `skills` only resolves once the assistant
 * platform flag is on. Exported for the same reason `normalizeSettingsTabParam` is
 * (see its own comment above): a direct unit test proves the flag-off fallback
 * without rendering this page's many hooks. Composes on top of
 * `normalizeSettingsTabParam` rather than folding into it, so that function's own
 * contract (and its existing test) stays about tab-name validity alone, never the flag.
 */
export function resolveSettingsTab(
  tab: string,
  assistantPlatformEnabled: boolean,
  connectorsEnabled = false,
): string {
  if (tab === "skills" && !assistantPlatformEnabled) {
    return "account";
  }
  if (tab === "connectors" && !connectorsEnabled) {
    return "account";
  }
  return tab;
}

export default function SettingsPage() {
  const { t } = useTranslation("app");
  const searchParams = useSearchParams();
  /* Frontend review BLOCKER 1: same hook every other new Assistant Platform v1 surface
     reads, so a flag-off org can never land on the Skills tab: not from the sidebar
     (already hidden, `Sidebar.tsx`), and not from a direct `/settings?tab=skills` URL. */
  const assistantPlatformEnabled = useAssistantPlatformEnabled();
  const connectorsEnabled = useAgentHandlerEnabled();
  const [activeTab, setActiveTab] = useState(() =>
    resolveSettingsTab(
      normalizeSettingsTabParam(searchParams.get("tab")),
      assistantPlatformEnabled,
      connectorsEnabled,
    ),
  );
  const [showInviteDialog, setShowInviteDialog] = useState(false);
  const [showTeamInviteUpgrade, setShowTeamInviteUpgrade] = useState(false);
  const [openingPortal, setOpeningPortal] = useState(false);

  async function openPortal() {
    setOpeningPortal(true)
    try {
      const { portalUrl } = await billingApi.getPortalUrl()
      window.open(portalUrl, '_blank', 'noopener,noreferrer')
    } catch (error: any) {
      toast.error(
        error?.response?.data?.message ?? 'Could not open billing portal. Try again.',
      )
    } finally {
      setOpeningPortal(false)
    }
  }

  // Profile hook
  const {
    profile,
    profileLoading,
    showEditDialog,
    setShowEditDialog,
    editName,
    setEditName,
    saving,
    initials,
    refreshProfile,
    handleRegenerateAvatar,
    canCustomizeAvatarColor,
    avatarAppearanceProps,
    handleSaveProfile,
    organization,
    editOrgType,
  } = useSettingsProfile();

  // Data hook (audiences moved to /audiences page)
  const {
    knowledge,
    knowledgeLoading,
    deletingKnowledgeId,
    handleDeleteKnowledge,
    formatDate,
    formatRelativeTime,
  } = useSettingsData(activeTab);
  useEffect(() => {
    const tab = searchParams.get("tab");
    const normalized = resolveSettingsTab(
      normalizeSettingsTabParam(tab),
      assistantPlatformEnabled,
      connectorsEnabled,
    );
    setActiveTab(normalized);
  }, [searchParams, assistantPlatformEnabled, connectorsEnabled]);

  const handleTabChange = (value: string) => {
    setActiveTab(value);
    // Update URL without triggering navigation/refresh
    window.history.replaceState(null, "", ROUTES.settingsTab(value));
  };

  const { data: members, isLoading: membersLoading } = useOrgMembers(organization?.id)
  const { data: invitesData, isLoading: invitesLoading } = useOrgInvites(organization?.id)

  const isAdmin = organization?.orgRole === 'owner' || organization?.orgRole === 'admin'

  const handleResendInvite = useCallback(async (inviteId: number) => {
    if (!organization) return
    await organizationsApi.resendInvite(organization.id, inviteId)
    mutate(TEAM_SWR_KEYS.invites(organization.id))
  }, [organization])

  const handleRevokeInvite = useCallback(async (inviteId: number) => {
    if (!organization) return
    await organizationsApi.revokeInvite(organization.id, inviteId)
    mutate(TEAM_SWR_KEYS.invites(organization.id))
    mutate(BILLING_SWR_KEYS.memberUsage(organization.id))
  }, [organization])

  const handleRemoveMember = useCallback(async (userId: string) => {
    if (!organization) return
    await organizationsApi.removeMember(organization.id, userId)
    mutate(TEAM_SWR_KEYS.members(organization.id))
    mutate(BILLING_SWR_KEYS.memberUsage(organization.id))
  }, [organization])

  const inviteMode = resolveInviteMode(
    profile?.subscriptionTier,
    organization?.orgRole,
  );

  const pageMeta: Record<string, { title: string; description: string }> = {
    account: { title: t('account.title'), description: t('account.description') },
    brand: { title: t('account.brandTitle'), description: t('account.brandDescription') },
    knowledge: { title: 'Library', description: 'Browse and manage your org library' },
    billing: { title: t('account.billingTitle'), description: t('account.billingDescription') },
    skills: { title: t('nav.llmSettings'), description: t('skillsSettings.pageDescription') },
  }

  const { title, description } = pageMeta[activeTab] ?? pageMeta.account

  const hasActiveSubscription = profile?.hasActiveSubscription ?? false
  const billingPortalAction = activeTab === 'billing' && hasActiveSubscription ? (
    <BillingLockedTooltip message="Contact customer support for invoices and billing changes.">
      <Button
        type="button"
        variant="cta"
        size="sm"
        shape="pill"
        onClick={openPortal}
        disabled
        data-testid="billing-cta-portal-header"
      >
        See billing and invoices
      </Button>
    </BillingLockedTooltip>
  ) : undefined

  // Library owns documents. The old settings knowledge tab redirects there.
  if (activeTab === 'knowledge') {
    return <Navigate to="/knowledge" replace />
  }
  // Connectors moved to the Library tab of the same name.
  if (activeTab === 'connectors') {
    return <Navigate to="/knowledge?view=connectors" replace />
  }

  return (
    <PageLayout className="max-w-full pt-14 py-8 md:pt-8">
      <PageHeader
        className="mt-12 md:mt-0"
        variant="utility"
        title={title}
        description={description}
        action={billingPortalAction}
      />

      <PageContent className="gap-8">
        <Tabs
          value={activeTab}
          onValueChange={handleTabChange}
          className="space-y-6"
        >
          <AccountTab
            profileLoading={profileLoading}
            profile={profile}
            organization={organization}
            canInvite={inviteMode !== 'hidden'}
            onEdit={() => setShowEditDialog(true)}
            onInvite={() => {
              if (inviteMode === 'invite') setShowInviteDialog(true);
              else if (inviteMode === 'upgrade') setShowTeamInviteUpgrade(true);
            }}
            onRegenerateAvatar={handleRegenerateAvatar}
            canCustomizeAvatarColor={canCustomizeAvatarColor}
            avatarAppearanceProps={avatarAppearanceProps}
            formatDate={formatDate}
            refreshProfile={refreshProfile}
            members={members}
            invites={invitesData}
            membersLoading={membersLoading || invitesLoading}
            isAdmin={isAdmin}
            currentUserId={profile?.id}
            onResendInvite={handleResendInvite}
            onRevokeInvite={handleRevokeInvite}
            onRemoveMember={handleRemoveMember}
          />

          <KnowledgeTab
            profile={profile}
            knowledgeLoading={knowledgeLoading}
            knowledge={knowledge}
            deletingKnowledgeId={deletingKnowledgeId}
            formatRelativeTime={formatRelativeTime}
            handleDeleteKnowledge={handleDeleteKnowledge}
          />

          {/* Brand Tab */}
          <BrandTab isAdmin={isAdmin} />

          {/* Skills Tab. Frontend review BLOCKER 1 (item 6): gated on the flag as well as
              `activeTab` (which `resolveSettingsTab` above already keeps off `skills` for a
              flag-off org), belt and suspenders, since mounting this at all is what fires
              `useSkillsTab`'s `GET /skills`, and the backend 403s a flag-off org. */}
          {assistantPlatformEnabled ? (
            <SkillsTab
              isAdmin={isAdmin}
              focusSkillSlug={searchParams.get('skill')}
              slackTwinsInstallResult={searchParams.get(SLACK_TWINS_INSTALL_RESULT_PARAM)}
            />
          ) : null}

          {/* Usage & Billing Tab */}
          <BillingTab profile={profile} onProfileUpdate={refreshProfile} onOpenPortal={openPortal} openingPortal={openingPortal} />
        </Tabs>

        <EditProfileDialog
          open={showEditDialog}
          onOpenChange={setShowEditDialog}
          editName={editName}
          onEditNameChange={setEditName}
          profileEmail={profile?.email || ""}
          saving={saving}
          onSave={handleSaveProfile}
          orgType={editOrgType}
          hasOrganization={!!organization}
        />

        {organization && (
          <InviteDialog
            open={showInviteDialog}
            onOpenChange={setShowInviteDialog}
            organizationToken={organization.id}
            organizationName={organization.name}
          />
        )}

        <TrialAudienceUpgradeModal
          open={showTeamInviteUpgrade}
          onClose={() => setShowTeamInviteUpgrade(false)}
          variant="team-invite"
          hasActiveSubscription={hasActiveSubscription}
        />
      </PageContent>
    </PageLayout>
  );
}
